import { GenericAbortSignal } from 'axios';

import http from 'services/http';

// A recorded portal-retrieval script. The index serves light rows; show
// adds the replayable payload (entryUrl + body) the browser extension executes.
export interface PortalScriptOptions {
  id: string;
  name: string;
  sourceSystem: string;
  createdAt: string;
  updatedAt: string;
}

export interface PortalScriptDetailOptions extends PortalScriptOptions {
  entryUrl: string;
  body: string;
  retrievalUrl: string | null;
}

// What a script does in the portal: `retrieval` pulls one referral's records, `discovery`
// sweeps the portal for new referral activity. The api defaults an unfiltered index to
// `retrieval`, so a sweep MUST ask for its own purpose — otherwise it silently replays a
// per-referral script against the portal home.
export type PortalScriptPurpose = 'retrieval' | 'discovery';

export const portalScriptQueryKeys = {
  base: ['portalScripts'] as const,
  // Keyed by purpose too: retrieval and discovery scripts for one source system are
  // different lists, and sharing a key would serve one flow the other's cache.
  list: (sourceSystem: string, purpose: PortalScriptPurpose = 'retrieval') =>
    ['portalScripts', 'list', sourceSystem, purpose] as const,
  show: (id: string) => ['portalScripts', 'show', id] as const,
};

export async function indexPortalScripts(
  sourceSystem: string,
  signal?: GenericAbortSignal,
  purpose: PortalScriptPurpose = 'retrieval'
) {
  return http
    .get<{ data: PortalScriptOptions[] }>(`portal_scripts`, {
      params: { source_system: sourceSystem, purpose },
      signal,
    })
    .then((res) => res.data.data);
}

export async function showPortalScript(id: string, signal?: GenericAbortSignal) {
  return http.get<PortalScriptDetailOptions>(`portal_scripts/${id}`, { signal }).then((res) => res.data);
}

// Archive the script a user recorded, so the retrieval card stops offering it for replay
// (the index serves only active rows). Bodies are immutable — re-recording is a new row —
// so archiving is how a stale script is retired. Owner-scoped by the api; an admin can
// restore from the Portal Automation settings surface.
export async function archivePortalScript(id: string) {
  return http.put<PortalScriptOptions>(`portal_scripts/${id}`, { archived: true }).then((res) => res.data);
}

// ---- Admin surface (admin → Clients → EditClient → Portal Automation) ----
// Every user's scripts for a client, light rows only — bodies are owner-only
// and never reach the admin surface. Management mirrors the owner surface:
// rename and archive/restore.

export interface AdminPortalScript extends PortalScriptOptions {
  purpose: PortalScriptPurpose;
  archivedAt: string | null;
  user: {
    id: string;
    name: string;
    email: string;
  };
}

export const adminPortalScriptQueryKeys = {
  base: ['adminPortalScripts'] as const,
  list: (clientId: string) => ['adminPortalScripts', 'list', clientId] as const,
};

export async function indexAdminPortalScripts(clientId: string, signal?: GenericAbortSignal) {
  return http
    .get<{ data: AdminPortalScript[] }>('admin/portal_scripts', { params: { client_id: clientId }, signal })
    .then((res) => res.data.data);
}

export type UpdateAdminPortalScriptInput = {
  id: string;
  name?: string;
  archived?: boolean;
};

export async function updateAdminPortalScript({ id, ...patch }: UpdateAdminPortalScriptInput) {
  return http.put<AdminPortalScript>(`admin/portal_scripts/${id}`, patch).then((res) => res.data);
}
